import {
    ClientAccountInformation,
    DataQualityAggregatedTimeseries,
    DataQualityOverallScores,
    ThirtyDayStatistics,
} from '@lune-fe/dapi-models'
import { truncateDateToLocalDate, useErrorSnackbar } from '@lune-fe/lune-components-lib'
import { LoadingWrapper, MainLayoutContainerNew } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import moment from 'moment'
import { FC, useEffect, useRef, useState } from 'react'

import WhiteBoxContainerWithContent from 'components/WhiteBoxContainerWithContent'
import {
    getDataQualityAggregatedTimeseries,
    getOverallDataHealthScores,
    getRecentClientAccounts,
    getThirtyDayStatistics,
} from 'endpoints/dapi'
import useAccounts from 'hooks/useAccounts'
import useActiveOrganisation from 'hooks/useActiveOrganisation'
import useIsTestMode from 'hooks/useIsTestMode'
import useTitle from 'hooks/useTitle'
import { SnackbarMessages } from 'SnackbarMessages'
import NewCalculationDropdown from 'views/CalculateEmissions/NewCalculationDropdown'
import { NavigationSource } from 'views/CalculateEmissions/Shared/BackButtonWithFromState'
import CalculationSources from 'views/Homepage/CalculationSources'
import ClientAccountsTable from 'views/Homepage/ClientAccountsTable'
import DataHealthScore from 'views/Homepage/DataHealthScore'
import EmptyHomepage from 'views/Homepage/EmptyHomepage'
import TopStats from 'views/Homepage/TopStats'
import WtwBreakdown from 'views/Homepage/WtwBreakdown'

const Homepage: FC = () => {
    useTitle('Lune Dashboard')
    const { activeAccount } = useAccounts()
    const { activeOrg } = useActiveOrganisation()
    const isTestMode = useIsTestMode()
    const { enqueueSnackbar: snackbar } = useErrorSnackbar()

    const [overallLoading, setOverallLoading] = useState(true)

    const [overallScore, setOverallScore] = useState<DataQualityOverallScores>()
    const [aggregatedScoreData, setAggregatedScoreData] =
        useState<DataQualityAggregatedTimeseries>()
    const [clientAccounts, setClientAccounts] = useState<ClientAccountInformation>()
    const [thirtyDayStats, setThirtyDayStats] = useState<ThirtyDayStatistics>()
    const [hasData, setHasData] = useState(false)

    const latestRequestIdRef = useRef<string | null>(null)

    const today = moment()
    const dataRange = {
        startDate: today.clone().subtract(30, `days`),
        endDate: today,
    }

    const from = truncateDateToLocalDate(dataRange.startDate.toDate())
    const through = truncateDateToLocalDate(dataRange.endDate.toDate())
    const interval = 'day'

    const getThirtyDaysRangeString = () => {
        const startMoment = dataRange.startDate
        const endMoment = dataRange.endDate
        const sameYear = startMoment.year() === endMoment.year()
        if (sameYear) {
            // Same year: "Jul 1 – Jul 30, 2025"
            return `${startMoment.format('MMM D')} – ${endMoment.format('MMM D, YYYY')}`
        } else {
            // Different years: "Dec 16, 2024 – Jan 15, 2025"
            return `${startMoment.format('MMM D, YYYY')} – ${endMoment.format('MMM D, YYYY')}`
        }
    }

    const resetHomepageState = () => {
        setOverallScore(undefined)
        setAggregatedScoreData(undefined)
        setClientAccounts(undefined)
        setThirtyDayStats(undefined)
        setHasData(false)
    }

    const hasDataFromDataHealth = (
        scores?: DataQualityOverallScores,
        aggregated?: DataQualityAggregatedTimeseries,
    ) => {
        if (!scores || !aggregated) {
            return false
        }

        const shipmentLegs = aggregated.byShipmentLegs.legs
        const hasModeData = Object.values(aggregated.byMode).some(
            (mode) => mode !== null && mode.legs > 0,
        )

        return shipmentLegs > 0 || hasModeData
    }

    const hasDataFromThirtyDayStats = (stats?: ThirtyDayStatistics) => {
        if (!stats) {
            return false
        }

        const numCalculations = stats.numCalculations.amount
        const quantity = Number(stats.quantity.amount) || 0
        const purchasedAmount = Number(stats.purchasedAmount.amount) || 0
        const offsetCost = Number(stats.offsetCost.amount) || 0
        const sourcesWithData = stats.sources.some((source) => source.count > 0)

        return (
            numCalculations > 0 ||
            quantity > 0 ||
            purchasedAmount > 0 ||
            offsetCost > 0 ||
            sourcesWithData
        )
    }

    const hasDataFromClientAccounts = (accounts?: ClientAccountInformation) =>
        !!accounts && accounts.topActiveClientAccounts.length > 0

    useEffect(() => {
        if (!activeOrg?.id || !activeAccount?.id) {
            latestRequestIdRef.current = null
            resetHomepageState()
            return
        }

        const requestId = `${activeOrg.id}-${activeAccount.id}-${Date.now()}`
        latestRequestIdRef.current = requestId

        setOverallLoading(true)
        resetHomepageState()

        const loadAllData = async () => {
            try {
                const overallPromise = Promise.all([
                    getOverallDataHealthScores(from, through),
                    getDataQualityAggregatedTimeseries({
                        from,
                        through,
                        interval,
                    }),
                ])
                    .then(([scores, aggregatedData]) => ({
                        scores,
                        aggregatedData,
                        error: false,
                    }))
                    .catch(() => ({
                        scores: undefined,
                        aggregatedData: undefined,
                        error: true,
                    }))

                const [overallPayload, clientAccountsResult, thirtyDayStatsResult] =
                    await Promise.all([
                        overallPromise,
                        getRecentClientAccounts(),
                        getThirtyDayStatistics(activeOrg.id),
                    ])

                if (latestRequestIdRef.current !== requestId) {
                    return
                }

                let nextHasData = false
                setOverallScore(overallPayload.scores)
                setAggregatedScoreData(overallPayload.aggregatedData)
                if (hasDataFromDataHealth(overallPayload.scores, overallPayload.aggregatedData)) {
                    nextHasData = true
                }

                // Client accounts
                if (clientAccountsResult.isOk()) {
                    const accounts = clientAccountsResult.value
                    setClientAccounts(accounts)

                    if (hasDataFromClientAccounts(accounts)) {
                        nextHasData = true
                    }
                } else {
                    snackbar(SnackbarMessages.GENERIC_ERROR, { error: clientAccountsResult.error })
                }

                // 30-day stats
                if (thirtyDayStatsResult.isOk()) {
                    const stats = thirtyDayStatsResult.value
                    setThirtyDayStats(stats)

                    if (hasDataFromThirtyDayStats(stats)) {
                        nextHasData = true
                    }
                } else {
                    snackbar(SnackbarMessages.GENERIC_ERROR, { error: thirtyDayStatsResult.error })
                }

                if (!nextHasData) {
                    // If nothing had meaningful data, clear any partials
                    resetHomepageState()
                } else {
                    setHasData(true)
                }
            } finally {
                if (latestRequestIdRef.current === requestId) {
                    setOverallLoading(false)
                }
            }
        }

        void loadAllData()

        return () => {
            if (latestRequestIdRef.current === requestId) {
                latestRequestIdRef.current = null
            }
        }
    }, [activeOrg?.id, activeAccount?.id])
    // activeAccount?.id is needed since it proves the primary account has
    // changed which all the endpoints being called above depend on implictly.

    return (
        <LoadingWrapper
            sx={{
                minHeight: '100vh',
            }}
            loading={overallLoading}
        >
            {!!activeOrg &&
                !!activeAccount &&
                (hasData ? (
                    <MainLayoutContainerNew
                        isTestMode={!!isTestMode}
                        title={`Welcome, ${activeAccount.name}!`}
                        subtitle={`A snapshot of ${activeOrg.name}’s performance and operational activity over the last 30 days (${getThirtyDaysRangeString()})`}
                        primaryButton={<NewCalculationDropdown from={NavigationSource.HOMEPAGE} />}
                    >
                        <Stack direction="column" sx={{ width: '100%' }} spacing={{ xs: 2 }}>
                            {thirtyDayStats && <TopStats stats={thirtyDayStats} />}

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                }}
                            >
                                {thirtyDayStats && (
                                    <WhiteBoxContainerWithContent
                                        title="WTW breakdown"
                                        sx={{ minHeight: '144px' }}
                                    >
                                        <WtwBreakdown
                                            breakdown={thirtyDayStats.logisticsWtwBreakdown}
                                        />
                                    </WhiteBoxContainerWithContent>
                                )}
                                {overallScore && aggregatedScoreData && (
                                    <WhiteBoxContainerWithContent
                                        title="Data quality"
                                        sx={{ minHeight: '144px' }}
                                        link={{
                                            href: '/data-health',
                                            label: 'View data health',
                                        }}
                                    >
                                        <DataHealthScore
                                            overallScores={overallScore}
                                            aggregatedScoreData={aggregatedScoreData}
                                        />
                                    </WhiteBoxContainerWithContent>
                                )}
                            </Box>

                            {thirtyDayStats && (
                                <WhiteBoxContainerWithContent title="Calculation sources">
                                    <CalculationSources data={thirtyDayStats.sources} />
                                </WhiteBoxContainerWithContent>
                            )}

                            {clientAccounts && (
                                <WhiteBoxContainerWithContent
                                    fullBody
                                    title="Client accounts"
                                    subTitle={'Top most recently active accounts'}
                                    sx={{
                                        height:
                                            clientAccounts.topActiveClientAccounts.length > 7
                                                ? '525px'
                                                : 'auto',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '> div:last-of-type': {
                                            height: '100%',
                                            overflow: 'hidden',
                                        },
                                    }}
                                >
                                    <ClientAccountsTable
                                        accounts={clientAccounts.topActiveClientAccounts}
                                    />
                                </WhiteBoxContainerWithContent>
                            )}
                        </Stack>
                    </MainLayoutContainerNew>
                ) : (
                    <EmptyHomepage />
                ))}
        </LoadingWrapper>
    )
}

export default Homepage
